import { create } from "zustand";
import config from "../config";
import { setAuthorization } from "../helpers/api_helper";
import { dcCustomersApi, customerRolesApi, passwordApi, adminMenuApi } from "../helpers/dck_helper";
import {
  flattenMenuSystems,
  ADMIN_MENU_QUERY_KEY,
  ADMIN_MENU_STALE_TIME,
} from "../helpers/menuContract";
import { queryClient } from "../query/QueryProvider";
import { stashHandoff, readHandoff, clearHandoff } from "../helpers/sessionHandoff";
import { MOCK_JWT_TOKEN } from "../constants/auth";
import { resetListFilters } from "./listFilterStore";

/**
 * 登入身分 — 全站唯一事實來源(zustand)。
 *
 * RBAC 角色、審核發動者、稽核紀錄都從這裡取「當前使用者」,不再硬編。
 * 目前為骨架:mock 模式在 App 啟動時建 dev session;正式登入 API 到位後,
 * 由登入頁呼叫 login(user, token) 即可,元件不改。
 *
 *   const user = useSessionStore((s) => s.user);
 *   useSessionStore.getState().login({ id, name, employeeId, role }, token);
 *   useSessionStore.getState().logout();
 */
const STORAGE_KEY = "authUser";
const TOKEN_KEY = "authToken";

// 「記住我」(ticket 10):勾=localStorage(關瀏覽器仍登入)、不勾=sessionStorage
// (關瀏覽器即登出)。session 只會活在其中一邊;寫入前先清另一邊,避免兩邊殘留。
function sessionHome() {
  try {
    if (sessionStorage.getItem(STORAGE_KEY)) return sessionStorage;
  } catch (e) { /* ignore */ }
  return localStorage;
}

function persist(user, storage = sessionHome()) {
  try {
    if (user) storage.setItem(STORAGE_KEY, JSON.stringify(user));
    else storage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* ignore(SSR/隱私模式) */
  }
}

function persistToken(token, storage = sessionHome()) {
  try {
    if (token) storage.setItem(TOKEN_KEY, token);
    else storage.removeItem(TOKEN_KEY);
  } catch (e) {
    /* ignore(SSR/隱私模式) */
  }
}

function clearBothStorages() {
  [localStorage, sessionStorage].forEach((s) => {
    try {
      s.removeItem(STORAGE_KEY);
      s.removeItem(TOKEN_KEY);
    } catch (e) { /* ignore */ }
  });
  clearHandoff(); // 換人／登出時交接槽也要清,不然下一個開的分頁會撿到舊身分
}

/** session 是不是住在 sessionStorage（＝「記住我」沒勾，每個分頁各一份） */
function sessionIsPerTab() {
  try {
    return !!sessionStorage.getItem(STORAGE_KEY);
  } catch (e) {
    return false;
  }
}

export const useSessionStore = create((set) => ({
  user: null, // { id, name, employeeId, role }
  isAuthenticated: false,

  // remember=false 時 session 存 sessionStorage(關瀏覽器即登出);預設 true 維持既有行為
  login: (user, token, { remember = true } = {}) => {
    clearBothStorages(); // 換邊登入不留殘影(殘留狀態面向)
    // 換身分就丟掉 GetMenu 快取(課題 20260831-01):真後端 GetMenu 對**未帶 token**
    // 的請求也回 200,但內容是匿名版(只有 5 個模組)。未登入時側欄誤抓的那份
    // 若還在 staleTime 內,assembleIdentity 的 fetchQuery 會判定新鮮直接沿用,
    // 首次登入/無痕就變成「選單缺一大半+整站沒權限」。這裡清掉,讓登入後
    // 一定用新 token 重抓;登出→換帳號重登的舊身分選單殘留也一併防掉。
    queryClient.removeQueries({ queryKey: ADMIN_MENU_QUERY_KEY });
    const storage = remember ? localStorage : sessionStorage;
    persist(user, storage);
    if (token) {
      persistToken(token, storage);
      setAuthorization(token);
    }
    set({ user, isAuthenticated: true });
  },

  logout: () => {
    clearBothStorages();
    setAuthorization(null);
    // 登出即丟選單快取:留著的話,登出停在登入頁(客端導頁、不重載)時,
    // 下一個登入的人會先看到上一個身分的選單(與 login 同一道防線)
    queryClient.removeQueries({ queryKey: ADMIN_MENU_QUERY_KEY });
    // 列表篩選條件同理(票 26):條件裡可能有人名、關鍵字,留著的話
    // 下一個登入的人一進列表就看到上一個身分篩到一半的東西。
    resetListFilters();
    set({ user: null, isAuthenticated: false });
  },

  // 局部更新當前身分(例如首登改完密碼後拿掉 mustChangePassword)
  updateUser: (patch) =>
    set((s) => {
      if (!s.user) return {};
      const user = { ...s.user, ...patch };
      persist(user);
      return { user };
    }),

  // 身分組裝(ADR-0002):GetToken 只回 customer_id,登入後用既有端點組合補齊——
  // DcCustomer/GetById → role_id/role_name → DcCustomerRole/GetById → modules/fields。
  // 任一步失敗不阻擋登入:permissions_source 標 "fallback",RBAC 屆時走靜態表。
  assembleIdentity: async () => {
    const { user } = useSessionStore.getState();
    if (!user || !user.id) return false;
    try {
      const customer = await dcCustomersApi.getById(user.id);
      const role = await customerRolesApi.getById(customer.role_id);
      // 首登改密/密碼逾期資料源(ticket 09):GET ChangePassword 模型。
      // 8/11 文件確認 DcCustomerDto 沒有 custom_properties,舊的 cp.must_change_password
      // 對真後端恆為 undefined。模型取不到不阻擋登入,保守視為不強制。
      let mustChangePassword = false;
      try {
        const pw = await passwordApi.getChangePasswordModel();
        mustChangePassword = !!(pw && (pw.password_must_be_changed || pw.password_expired));
      } catch (e) {
        /* 保守 false */
      }
      // GetMenu 也一起收:swagger 明寫它是「依目前登入者權限過濾」的,
      // 所以它是**第二個**後端權限來源。8/14 實測兩邊會打架(角色 modules 整批
      // Hidden、選單卻整份可見),RBAC 要看得到兩邊才判得出來(見 ADR-0002 補述)。
      // 取不到不阻擋登入 —— 那只是少了降級來源,不是登不進去。
      //
      // ⚠️ 走 `queryClient.fetchQuery` 而不是直接 `adminMenuApi.getMenu()`:
      // 側欄(`useAdminMenu`)用的是同一個 queryKey。直接打 axios 的話兩邊各打一次,
      // **每次登入 GetMenu 都會送出兩個一模一樣的請求**(8/18 實測:兩次都 200)。
      // 用 fetchQuery 就會共用快取 —— 誰先到誰發請求,另一邊直接命中,
      // 而且 staleTime 由 `useAdminMenu` 那份設定統一管(5 分鐘)。
      let menuSystems = [];
      try {
        const menu = await queryClient.fetchQuery({
          queryKey: ADMIN_MENU_QUERY_KEY,
          queryFn: () => adminMenuApi.getMenu(),
          staleTime: ADMIN_MENU_STALE_TIME,
        });
        menuSystems = [...flattenMenuSystems(menu)];
      } catch (e) {
        // 降級要吵(ADR-0004):這裡靜靜寫 [] 的話,側欄稍後打成功、守衛仍拿空 snapshot,
        // 畫面就是「選單有、內容說尚未開通」。
        console.warn(
          "[RBAC] 身分組裝時 GetMenu 失敗,session.menu_systems 將是空的;",
          "守衛改讀側欄同一把快取,選單稍後回來就會重算。",
          e
        );
      }
      const enriched = {
        ...user,
        name: customer.name || user.name,
        employeeId: customer.employee_no || user.employeeId,
        mustChangePassword, // 首登改密流程(ticket 34→09,Header 強制彈窗以此觸發)
        role: role.system_name || user.role, // 前端四角色 key(Overseer 等)
        role_id: customer.role_id,
        role_name: customer.role_name,
        permissions: {
          modules: role.modules || [],
          fields: role.fields || [],
          menu_systems: menuSystems,
        },
        permissions_source: "backend",
      };
      persist(enriched);
      set({ user: enriched });
      return true;
    } catch (e) {
      const fallback = { ...user, permissions_source: "fallback" };
      persist(fallback);
      set({ user: fallback });
      return false;
    }
  },

  // 開新分頁前的交接(轉派彈窗的箭頭是 target="_blank")。
  // 「記住我」有勾時 session 在 localStorage,跨分頁本來就通,不必交接;
  // 沒勾時 session 在 sessionStorage(每分頁一份),新分頁開起來是空的 ——
  // 放一份 30 秒有效的交接資料,讓新分頁 hydrate 得起來,不用重登。
  // 回傳有沒有交接(給呼叫端與測試看),沒有 token 就不交接。
  prepareNewTab: () => {
    const { user } = useSessionStore.getState();
    if (!user || !sessionIsPerTab()) return false;
    let token = null;
    try {
      token = sessionStorage.getItem(TOKEN_KEY);
    } catch (e) { /* ignore */ }
    return stashHandoff(user, token);
  },

  // dev 角色切換器用:改當前使用者角色。
  // 移除身分組裝殘留的後端權限,讓 RBAC 走切換後角色的靜態表(否則權限仍是舊角色的)。
  setRole: (role) =>
    set((s) => {
      const base = { ...(s.user || { id: 0, name: "開發者" }) };
      delete base.permissions;
      const user = { ...base, role, permissions_source: "fallback" };
      persist(user);
      return { user, isAuthenticated: true };
    }),

  // 從 localStorage 還原 session(重新整理後保持登入,含 JWT → Authorization header)
  // 依「記住我」的選擇,session 可能在 localStorage(勾)或 sessionStorage(不勾)
  hydrate: () => {
    try {
      const storage = sessionHome();
      const raw = storage.getItem(STORAGE_KEY);
      if (raw) {
        const token = storage.getItem(TOKEN_KEY);
        // hybrid(真後端)下拒絕還原全 mock 時期的殘留 session:
        // 假 token/無 token 打真後端必 401;只逛 mock 模組時假身分還會一直掛著。
        // 清掉並要求重新走真登入(mp-code-review Spec #1)。
        if (config.BACKEND_PASSTHROUGH && (!token || token === MOCK_JWT_TOKEN)) {
          clearBothStorages();
          return false;
        }
        const user = JSON.parse(raw);
        if (token) setAuthorization(token);
        set({ user, isAuthenticated: true });
        return true;
      }
      // 兩邊都沒有 → 看看是不是「從既有分頁開出來的新分頁」(轉派彈窗的箭頭)。
      // 有交接資料就在這個分頁重建 session,不用再登入一次。
      const handed = readHandoff();
      if (handed) {
        // 與上面同一道防線:hybrid(真後端)下不接受 mock token 復活的假身分
        if (config.BACKEND_PASSTHROUGH && handed.token === MOCK_JWT_TOKEN) {
          clearHandoff();
          return false;
        }
        // 寫回 sessionStorage 而不是 localStorage —— 來源分頁當初沒勾「記住我」,
        // 交接不該把那個選擇翻掉(關掉瀏覽器仍要登出)。
        persist(handed.user, sessionStorage);
        persistToken(handed.token, sessionStorage);
        setAuthorization(handed.token);
        set({ user: handed.user, isAuthenticated: true });
        return true;
      }
    } catch (e) {
      /* ignore */
    }
    return false;
  },
}));
